import React from 'react'
import {Modal} from 'react-bootstrap'
import {KTSVG} from '../../../_metronic/helpers'
import {BatchItem, ItemDraft} from './bulkVosTypes'
import {
  formatBulkVosCallId,
  formatBulkVosRecordId,
  formatBulkVosGridTime,
  formatBulkVosTimeInput,
  formatMileageOrig,
  formatTravelOrig,
} from './bulkVosClientPortalFormat'

const inputClass =
  'form-control form-control-sm form-control-white custom-input-height'

type Props = {
  show: boolean
  item: BatchItem | null
  draft: ItemDraft
  readOnly: boolean
  /** Client portal vs finance billing review */
  variant?: 'client' | 'finance'
  showDisputeBadge?: boolean
  saving?: boolean
  onHide: () => void
  onDraftChange: (patch: Partial<ItemDraft>) => void
  onCancelDispute?: () => void
  onSave?: () => void
  onFinanceMarkHandled?: () => void
  onFinanceClearDispute?: () => void
}

export function BulkVosDisputeModal({
  show,
  item,
  draft,
  readOnly,
  variant = 'client',
  showDisputeBadge,
  saving = false,
  onHide,
  onDraftChange,
  onCancelDispute,
  onSave,
  onFinanceMarkHandled,
  onFinanceClearDispute,
}: Props) {
  if (!item) return null

  const callLabelRaw = formatBulkVosCallId(item.callId)
  const callLabel = callLabelRaw !== '—' ? callLabelRaw : String(item.id)
  const disputeBadge = showDisputeBadge ?? item.isDisputed
  const isFinance = variant === 'finance'

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      backdrop='static'
      dialogClassName='mw-900px'
      enforceFocus={false}
    >
      <div className='modal-header py-2'>
        <h2 className='fw-semibold fs-3 mb-0'>Adjust Call Details: {callLabel}</h2>
        <div className='d-flex align-items-center gap-3 ms-3 ms-md-auto'>
          {disputeBadge && (
            <span className='badge badge-light-danger fs-8 fw-bold'>Dispute active</span>
          )}
          <div
            className='btn btn-icon btn-sm btn-active-icon-primary'
            onClick={onHide}
            role='button'
            tabIndex={0}
            aria-label='Close'
            onKeyDown={(e) => e.key === 'Enter' && onHide()}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-1' />
          </div>
        </div>
      </div>

      <Modal.Body className='modal-body scroll-y pt-2 pb-6 px-9'>
        <p className='text-muted fs-7 mb-6'>
          Modify start/end times and specify mandatory compliance remarks for the selected record.
        </p>

        <div className='rounded border border-gray-300 bg-light p-5 mb-6'>
          <div className='row g-4'>
            <div className='col-sm-4'>
              <div className='text-muted fs-8 fw-bold text-uppercase mb-1'>Requester</div>
              <div className='fw-semibold fs-6 text-gray-800'>{item.requesterName ?? '—'}</div>
            </div>
            <div className='col-sm-4'>
              <div className='text-muted fs-8 fw-bold text-uppercase mb-1'>Record ID</div>
              <div className='fw-semibold fs-6 text-gray-800'>{formatBulkVosRecordId(item.recordId)}</div>
            </div>
            <div className='col-sm-4'>
              <div className='text-muted fs-8 fw-bold text-uppercase mb-1'>Communication type</div>
              <div className='fw-semibold fs-6 text-primary'>
                {item.communicationTypeLabel ?? '—'}
              </div>
            </div>
          </div>
        </div>

        <div className='row g-4 g-xl-6 mb-2'>
          <div className='col-md-6'>
            <label className='form-label fs-7 mb-1 d-flex flex-wrap justify-content-between gap-1'>
              <span>Editable start time</span>
              <span className='text-muted fw-normal fs-8'>
                Original:{' '}
                {formatBulkVosGridTime(draft.originalStartTime ?? item.startTime)}
              </span>
            </label>
            <input
              className={inputClass}
              disabled={readOnly}
              placeholder='e.g. 08:27 PM'
              value={formatBulkVosTimeInput(draft.editableStartTime)}
              onChange={(e) => onDraftChange({editableStartTime: e.target.value})}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label fs-7 mb-1 d-flex flex-wrap justify-content-between gap-1'>
              <span>Editable end time</span>
              <span className='text-muted fw-normal fs-8'>
                Original: {formatBulkVosGridTime(draft.originalEndTime ?? item.endTime)}
              </span>
            </label>
            <input
              className={inputClass}
              disabled={readOnly}
              placeholder='e.g. 10:32 PM'
              value={formatBulkVosTimeInput(draft.editableEndTime)}
              onChange={(e) => onDraftChange({editableEndTime: e.target.value})}
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label fs-7 mb-1 d-flex flex-wrap justify-content-between gap-1'>
              <span>Mileage</span>
              <span className='text-muted fw-normal fs-8'>
                Original: {formatMileageOrig(draft.originalMilage)}
              </span>
            </label>
            <input
              className={inputClass}
              type='number'
              disabled={readOnly}
              value={draft.editableMilage ?? ''}
              onChange={(e) =>
                onDraftChange({
                  editableMilage: e.target.value === '' ? undefined : Number(e.target.value),
                })
              }
            />
          </div>
          <div className='col-md-6'>
            <label className='form-label fs-7 mb-1 d-flex flex-wrap justify-content-between gap-1'>
              <span>Travel time</span>
              <span className='text-muted fw-normal fs-8'>
                Original:{' '}
                {formatTravelOrig(draft.originalTravelTimeH, draft.originalTravelTimeM)}
              </span>
            </label>
            <div className='d-flex align-items-center flex-wrap gap-3'>
              <div className='d-flex align-items-center gap-2'>
                <input
                  className={inputClass}
                  style={{width: 72}}
                  type='number'
                  disabled={readOnly}
                  value={draft.editableTravelTimeH ?? ''}
                  onChange={(e) =>
                    onDraftChange({
                      editableTravelTimeH:
                        e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                />
                <span className='text-muted fs-8 fw-semibold'>Hours</span>
              </div>
              <div className='d-flex align-items-center gap-2'>
                <input
                  className={inputClass}
                  style={{width: 72}}
                  type='number'
                  disabled={readOnly}
                  value={draft.editableTravelTimeM ?? ''}
                  onChange={(e) =>
                    onDraftChange({
                      editableTravelTimeM:
                        e.target.value === '' ? undefined : Number(e.target.value),
                    })
                  }
                />
                <span className='text-muted fs-8 fw-semibold'>Minutes</span>
              </div>
            </div>
          </div>
        </div>

        <div className='mb-2'>
          <label className='form-label fs-7 mb-1'>{isFinance ? 'Finance notes' : 'Remarks'}</label>
          <p className='text-muted fs-8 mb-2'>
            {isFinance
              ? 'Set final billing times and notes, then save. Mark handled when the dispute is resolved.'
              : 'Please mention remarks if its anything else than adjustment of time, team will review and adjust accordingly'}
          </p>
          <textarea
            className='form-control form-control-sm form-control-white'
            rows={3}
            disabled={readOnly}
            value={draft.remarks ?? ''}
            onChange={(e) => onDraftChange({remarks: e.target.value})}
            placeholder='Explain what should be corrected for billing.'
          />
        </div>
      </Modal.Body>

      <div className='modal-footer py-3 px-9'>
        {!readOnly && !isFinance && disputeBadge && onCancelDispute && (
          <button
            type='button'
            className='btn btn-sm btn-light-danger me-auto'
            onClick={onCancelDispute}
          >
            Cancel dispute flag
          </button>
        )}
        {!readOnly && isFinance && onFinanceClearDispute && (
          <button
            type='button'
            className='btn btn-sm btn-light-danger me-auto'
            disabled={saving}
            onClick={onFinanceClearDispute}
          >
            Clear dispute flag
          </button>
        )}
        <button type='button' className='btn btn-light btn-sm me-3' onClick={onHide}>
          Close
        </button>
        {!readOnly && !isFinance && onSave && (
          <button type='button' className='btn btn-primary btn-sm' onClick={onSave} disabled={saving}>
            {saving ? 'Submitting…' : 'Submit dispute'}
          </button>
        )}
        {!readOnly && isFinance && onFinanceMarkHandled && (
          <button
            type='button'
            className='btn btn-primary btn-sm'
            onClick={onFinanceMarkHandled}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save & mark handled'}
          </button>
        )}
      </div>
    </Modal>
  )
}
